/*
 * Gifts on quantity — behavioral JS for the <quantity-gifts> custom element.
 * Built on Dawn conventions (pubsub, fetchConfig, routes) and the global cart
 * guard (window.DD0Gifts).
 *
 * Each gift unlocks when the selected quantity of the main product reaches its
 * threshold (data-quantity). Unlocked gifts are added to the cart when the
 * customer presses add-to-cart (deduped against the live cart, marked with the
 * _dd0_free_gift property). Removal when the quantity drops below the
 * threshold — and forcing quantity 1 / removing duplicates — is delegated to
 * dd0-gift-guard.js, which enforces the registered goals on every cart change.
 */

if (!customElements.get('quantity-gifts')) {
  customElements.define(
    'quantity-gifts',
    class QuantityGifts extends HTMLElement {
      connectedCallback() {
        if (this.connected) return;
        this.connected = true;
        this.visualsInitialized = false;
        const announcement = this.querySelector('[data-gift-announcement]');
        if (announcement) announcement.textContent = '';
        this.mainProduct = this.dataset.mainProduct || '';
        this.gifts = Array.from(this.querySelectorAll('.quantity-gift')).map((element) => ({
          element,
          kind: element.dataset.kind === 'benefit' ? 'benefit' : 'product',
          variantId: parseInt(element.dataset.product, 10) || null,
          handle: element.dataset.handle || '',
          threshold: parseInt(element.dataset.quantity, 10) || 1,
        }));
        if (!this.gifts.length) return;

        this.pendingAdd = null;
        this.resubmitting = false;

        this.registerGoals();

        this.onSubmit = this.onProductFormSubmit.bind(this);
        // window, not document: <qs-breaks> registers its own capture listener
        // on document and upgrades first (its script is loaded before this one).
        // Its mixed-variant bundle path calls stopImmediatePropagation() without
        // resubmitting, which would drop the gifts entirely. The window capture
        // phase runs first, so the gifts are in the cart before it takes over.
        if (this.gifts.some((gift) => gift.kind === 'product' && gift.variantId && gift.handle)) {
          window.addEventListener('submit', this.onSubmit, true);
        }

        // The lock state follows the quantity picker in real time (native
        // change + the theme's quantityUpdate/variantChange pubsub below).
        this.onQuantityChange = this.updateVisuals.bind(this);
        document.addEventListener('change', this.onQuantityChange);

        // pubsub: quantity + variant changes (product-info.js). typeof guards —
        // subscribe/PUB_SUB_EVENTS are top-level declarations, not window props.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          if (PUB_SUB_EVENTS.quantityUpdate) {
            this.unsubscribeQuantity = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.onQuantityChange);
          }
          if (PUB_SUB_EVENTS.variantChange) {
            this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, this.onQuantityChange);
          }
        }

        this.updateVisuals();
      }

      disconnectedCallback() {
        this.connected = false;
        window.removeEventListener('submit', this.onSubmit, true);
        document.removeEventListener('change', this.onQuantityChange);
        if (this.unsubscribeQuantity) this.unsubscribeQuantity();
        if (this.unsubscribeVariant) this.unsubscribeVariant();
      }

      // Register each gift's rule with the global guard so it can remove the
      // gift line (and force quantity 1 / drop duplicates) whenever the main
      // product drops below the threshold, on any page.
      registerGoals() {
        if (!window.DD0Gifts || !this.mainProduct) return;
        this.gifts.forEach((gift) => {
          if (gift.kind !== 'product' || !gift.handle || !gift.variantId) return;
          window.DD0Gifts.registerGoal({
            giftHandle: gift.handle,
            giftVariantId: gift.variantId,
            requiredQuantity: gift.threshold,
            mainProduct: this.mainProduct,
            kind: 'gift',
          });
        });
      }

      selectedQuantity() {
        const scope = this.closest('product-info') || document;
        const form = scope.querySelector('form[action*="/cart/add"]');
        if (!form) return 1;
        const value = parseInt(new FormData(form).get('quantity'), 10);
        return value > 0 ? value : 1;
      }

      // The lock state reflects only the quantity being selected, not what is
      // already in the cart.
      updateVisuals() {
        const preview = this.selectedQuantity();
        const announcements = [];
        this.gifts.forEach((gift) => {
          const unlocked = preview >= gift.threshold;
          const changed = gift.element.dataset.unlocked !== String(unlocked);
          gift.element.classList.toggle('quantity-gift--unlocked', unlocked);
          gift.element.dataset.unlocked = String(unlocked);
          const overlay = gift.element.querySelector('[data-gift-overlay]');
          if (overlay) overlay.hidden = unlocked;
          const state = gift.element.querySelector('[data-gift-state]');
          const label = unlocked ? this.dataset.unlockedLabel : this.dataset.lockedLabel;
          if (state && label) state.textContent = label;
          if (changed && this.visualsInitialized && label) {
            const title = gift.element.querySelector('.quantity-gift__title')?.textContent.trim();
            announcements.push(`${title}: ${label}`);
          }
        });
        const announcement = this.querySelector('[data-gift-announcement]');
        if (announcement && announcements.length) announcement.textContent = announcements.join('. ');
        this.visualsInitialized = true;
        this.dataset.initialized = 'true';
      }

      // Intercepts the main product's form submit: adds the unlocked gifts
      // first (no section render), then resubmits so Dawn's product-form paints
      // the main product + gifts in a single update.
      onProductFormSubmit(event) {
        if (this.resubmitting) {
          this.resubmitting = false;
          return;
        }
        // Re-click while the gifts are being added: swallow it, otherwise it
        // reaches product-form.js and adds the main product without gifts (and a
        // second time once the queued resubmit lands).
        if (this.pendingAdd) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        // <qs-breaks> resubmits the form after adding its own extras: the gifts
        // are already in the cart, so let that submit through instead of running
        // a second dedupe round trip.
        if (this.lastAddAt && Date.now() - this.lastAddAt < 2000) return;
        // Hidden by the device-visibility setting: don't add invisible gifts.
        if (this.offsetParent === null) return;
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !form.action.includes('/cart/add')) return;
        const scope = this.closest('product-info');
        if (scope && !scope.contains(form)) return;
        const productForm = form.closest('product-form');
        if (!productForm) return;
        const submitButton = productForm.querySelector('[type="submit"]');
        if (submitButton && submitButton.getAttribute('aria-disabled') === 'true') return;

        const predictedUnits = parseInt(new FormData(form).get('quantity'), 10) || 1;
        const gifts = this.gifts.filter(
          (gift) => gift.kind === 'product' && gift.variantId && gift.handle && predictedUnits >= gift.threshold
        );
        if (!gifts.length) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        // Full Dawn loading state: the class alone hides the label without
        // showing anything in its place.
        const spinner = productForm.querySelector('.loading__spinner');
        if (submitButton) {
          submitButton.classList.add('loading');
          submitButton.setAttribute('aria-disabled', 'true');
        }
        spinner?.classList.remove('hidden');
        const submitter = event.submitter;
        this.pendingAdd = this.addMissingGifts(gifts)
          .catch((error) => console.warn('[quantity-gifts] add error', error))
          .then(() => {
            this.pendingAdd = null;
            this.lastAddAt = Date.now();
            if (submitButton) submitButton.removeAttribute('aria-disabled');
            this.resubmitting = true;
            if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter || undefined);
            else form.submit();
          });
      }

      // Deduplicates against the real cart read at click time — no client-side
      // cache that could go stale.
      async addMissingGifts(gifts) {
        gifts = gifts.filter((gift) => gift.kind === 'product' && gift.variantId && gift.handle);
        if (!gifts.length) return;
        const cart = await fetch(`${routes.cart_url}.js`, { cache: 'no-store' }).then((response) =>
          response.json()
        );
        const inCart = new Set(cart.items.map((item) => item.variant_id));
        const missing = gifts.filter((gift) => !inCart.has(gift.variantId));
        if (!missing.length) return;

        const items = missing.map((gift) => ({
          id: gift.variantId,
          quantity: 1,
          // Marks the line as an auto-added gift and records the unlock rule so
          // the guard can enforce removal on any cart line.
          properties: {
            _dd0_free_gift: true,
            _dd0_gift_parent: this.mainProduct,
            _dd0_gift_min: gift.threshold,
          },
        }));
        const response = await fetch(routes.cart_add_url, {
          ...fetchConfig(),
          body: JSON.stringify({ items }),
        }).then((response) => response.json());
        // Multi-item adds are all-or-nothing: a single sold-out gift blocks
        // every gift. Surface it for debugging.
        if (response.status) {
          console.warn('[quantity-gifts] gifts not added:', response.description || response.message);
        }
      }
    }
  );
}
